import { Entry } from "contentful";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/router";
import { useTranslations } from "next-intl";
import { useSetRecoilState } from "recoil";

import { countryDialogOpenedState } from "~/atoms/countryDialog";
import Editorials from "~/types/editorials";
import Constants from "~/utils/constants";
import crossCountryUtils from "~/utils/crossCountry-utils";
import { assertEditorialType, isActiveEntry } from "~/utils/editorial-utils";
import textUtils from "~/utils/text-utils";

import Icon from "../common/icon";
import MediaAsset from "../common/media-asset";
import styles from "./mini-footer.module.scss";

type Props = {
  entry: Entry<unknown>;
};

export default function MiniFooter(props: Props) {
  const t = useTranslations();
  const router = useRouter();
  const languageCode = crossCountryUtils.getCurrentLanguageCode(router);
  const countryCode = crossCountryUtils.getCurrentCountryCode(router);
  const setCountryDialogOpenedState = useSetRecoilState(
    countryDialogOpenedState
  );

  function openCountryDialog() {
    setCountryDialogOpenedState(true);
  }
  assertEditorialType<Editorials.MiniFooter>(props.entry, "footer");

  const { socialLinks, legalLinks, appleAppStoreLink, googlePlayStoreLink } =
    props.entry.fields;

  return (
    <footer className={styles.footer}>
      <div className={styles.containerLinksAndCountryDialog}>
        <div className={styles.socialLinksContainer}>
          <span className={styles.followUsOnLabel}>
            {t("generic.follow_us_on")}
          </span>

          {socialLinks?.filter(isActiveEntry).map((socialLink, i) => {
            const { icon, openOnANewTab, text } = socialLink.fields;
            if (!isActiveEntry(icon!)) return null;

            const { svgIconId, svgMedia } = icon?.fields!;
            return (
              <Link
                prefetch={false}
                key={`${socialLink.sys.id}-mini-footer-${i}`}
                href={textUtils.sanitizeContentfulUrl(socialLink, router)}
                target={openOnANewTab ? "_blank" : undefined}
              >
                <Icon svgMedia={svgMedia} name={svgIconId} />
              </Link>
            );
          })}
        </div>
        {appleAppStoreLink || googlePlayStoreLink ? (
          <div className={styles.media}>
            {appleAppStoreLink &&
            appleAppStoreLink.fields.image &&
            isActiveEntry(appleAppStoreLink) ? (
              <div className={styles.mediaApple}>
                <Link
                  prefetch={false}
                  href={textUtils.sanitizeContentfulUrl(
                    appleAppStoreLink,
                    router
                  )}
                  target={
                    appleAppStoreLink.fields.openOnANewTab
                      ? "_blank"
                      : undefined
                  }
                >
                  <MediaAsset entry={appleAppStoreLink.fields.image} />
                </Link>
              </div>
            ) : null}
            {googlePlayStoreLink &&
            googlePlayStoreLink.fields.image &&
            isActiveEntry(googlePlayStoreLink) ? (
              <div className={styles.mediaGoogle}>
                <Link
                  prefetch={false}
                  href={textUtils.sanitizeContentfulUrl(
                    googlePlayStoreLink,
                    router
                  )}
                  target={
                    googlePlayStoreLink.fields.openOnANewTab
                      ? "_blank"
                      : undefined
                  }
                >
                  <MediaAsset entry={googlePlayStoreLink.fields.image} />
                </Link>
              </div>
            ) : null}
          </div>
        ) : null}
        <div className={styles.countryContainer}>
          <div className={styles.countryName}>
            <span className={styles.fontBase}>{t("generic.country")}</span>
            <span className={styles.fontHighlighted}>{countryCode}</span>
            <Image
              unoptimized
              width={16}
              height={16}
              alt={countryCode}
              src={`/next/assets/flags/${countryCode.toLowerCase()}.svg`}
            />
          </div>
          <div className={styles.countryLanguage}>
            <span className={styles.fontBase}>{t("generic.language")}</span>
            <span className={styles.fontHighlighted}>{languageCode}</span>
          </div>
          <button
            onClick={() => openCountryDialog()}
            className={styles.countryChange}
          >
            <span className={styles.fontBase}>{t("generic.change")}</span>
            <Icon name="arrow-right" width={16} height={16} />
          </button>
        </div>
      </div>
      <div className={styles.legalContainer}>
        <p className={styles.legalText}>
          <span>{t("generic.footer_legal_text")}</span>

          {legalLinks?.filter(isActiveEntry).map((legalLink, i) => {
            const { openOnANewTab, text } = legalLink.fields;

            return (
              <span key={`${legalLink.sys.id}-mini-footer-${i}`}>
                {legalLink.fields.url === Constants.DIDOMI_SPECIAL_LINK ? (
                  <button
                    className={styles.legalLink}
                    onClick={() => window?.Didomi?.preferences.show()}
                  >
                    {text}
                  </button>
                ) : (
                  <Link
                    className={styles.legalLink}
                    prefetch={false}
                    href={textUtils.sanitizeContentfulUrl(legalLink, router)}
                    target={openOnANewTab ? "_blank" : undefined}
                  >
                    {text}
                  </Link>
                )}
              </span>
            );
          })}
        </p>
      </div>
    </footer>
  );
}
